import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Bell, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../services/api';

const ROLE_MESSAGES = {
  va: 'Get notified when new tasks are assigned to you.',
  client: 'Get notified when your VA completes tasks.',
  admin: 'Stay updated on leave requests and VA activity.',
};

const LOCAL_KEY = 'nimble_notif_triggered';

/**
 * Call this after meaningful user actions to trigger the onboarding prompt.
 * Lightweight: just sets a localStorage flag. The component checks server state.
 */
export function triggerNotificationPrompt() {
  if ('Notification' in window && Notification.permission === 'granted') return;
  localStorage.setItem(LOCAL_KEY, 'true');
}

/**
 * Show a browser notification (if permission granted).
 * Used by NotificationBell when new notifications arrive.
 */
export function showBrowserNotification(title, body, link) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const notif = new Notification(title, {
      body,
      icon: 'https://customer-assets.emergentagent.com/job_virtual-admin-1/artifacts/zwdakbm3_New%20Logo%20HD.png',
      tag: `nimble-${Date.now()}`,
    });
    if (link) {
      notif.onclick = () => {
        window.focus();
        window.location.href = link;
      };
    }
  } catch {
    // Silent fail for environments that don't support Notification constructor
  }
}

export default function NotificationOnboarding() {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role || 'client';
  const message = ROLE_MESSAGES[role] || ROLE_MESSAGES.client;
  const token = localStorage.getItem('token');

  const checkShouldShow = useCallback(async () => {
    // Not logged in
    if (!token) return;

    // Browser doesn't support notifications or already granted
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') return;

    // No trigger action yet
    if (!localStorage.getItem(LOCAL_KEY)) return;

    // Check server-side preference
    try {
      const res = await apiClient.get('/in-app-notifications/preferences');
      const prefs = res.data;
      if (prefs.onboarding_completed) return;
      if (prefs.onboarding_dismissed) return;
      if (prefs.onboarding_snoozed_until) {
        if (new Date() < new Date(prefs.onboarding_snoozed_until)) return;
      }
      setVisible(true);
    } catch {
      // If API fails, fall back to showing prompt (first-time users)
      setVisible(true);
    }
  }, [token]);

  useEffect(() => {
    // Initial check after a short delay (don't interrupt page load)
    const timer = setTimeout(checkShouldShow, 2000);
    return () => clearTimeout(timer);
  }, [checkShouldShow]);

  // Also re-check when localStorage trigger fires (from other components)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === LOCAL_KEY) checkShouldShow();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [checkShouldShow]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          await apiClient.post('/in-app-notifications/preferences/enable-push');
          showBrowserNotification('Notifications Enabled', 'You\'ll now receive updates from Nimble Assistants.');
        } else {
          await apiClient.post('/in-app-notifications/preferences/dismiss-onboarding');
        }
      }
    } catch {
      // Silent fail
    } finally {
      localStorage.removeItem(LOCAL_KEY);
      setLoading(false);
      setVisible(false);
    }
  };

  const handleSnooze = async () => {
    try {
      await apiClient.post('/in-app-notifications/preferences/snooze-onboarding');
    } catch {
      // Silent fail
    }
    localStorage.removeItem(LOCAL_KEY);
    setVisible(false);
  };

  const handleDismiss = async () => {
    try {
      await apiClient.post('/in-app-notifications/preferences/dismiss-onboarding');
    } catch {
      // Silent fail
    }
    localStorage.removeItem(LOCAL_KEY);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-6 right-6 z-[60] max-w-sm"
          data-testid="notification-onboarding-prompt"
        >
          <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-5 relative">
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 text-[#9CA3AF] hover:text-[#4B5563] transition-colors"
              data-testid="notification-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3">
              <div className="bg-[#E5F7E7] rounded-lg p-2.5 flex-shrink-0">
                <Bell className="w-5 h-5 text-[#5FD068]" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-[#0A0D0A] mb-1">
                  Enable push notifications?
                </h4>
                <p className="text-xs text-[#4B5563] leading-relaxed mb-4">
                  {message}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="bg-[#5FD068] hover:bg-[#4BB854] text-white text-xs h-8 px-4"
                    onClick={handleEnable}
                    disabled={loading}
                    data-testid="notification-enable-btn"
                  >
                    {loading ? 'Enabling...' : 'Enable'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[#4B5563] text-xs h-8 px-3 hover:bg-[#F3F4F6]"
                    onClick={handleSnooze}
                    data-testid="notification-snooze-btn"
                  >
                    Maybe later
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
